import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiInfo, FiLayout, FiDownload, FiZap, FiHeart } = FiIcons;

const features = [
  {
    icon: FiLayout,
    title: "Designed For Real Posters",
    description: "Carefully crafted layouts that look perfect on digital screens and also print beautifully on paper and frames."
  },
  {
    icon: FiZap,
    title: "Create In Seconds",
    description: "Type a quote, pick a style, download instantly — no design skills, no complicated editors, no wasted time."
  },
  {
    icon: FiDownload,
    title: "Print & Share Quality",
    description: "Generate clean high-resolution quote posters suitable for digital frames, wall prints, study desks, and social media."
  }
];

function AboutUs() {
  return (
    <div className="bg-white min-h-screen">
      <div className="container mx-auto px-4 py-16 max-w-4xl">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-2xl mb-6">
            <SafeIcon icon={FiInfo} className="w-8 h-8 text-indigo-600" />
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-4">About QuotePoster.app</h1>
          <p className="text-xl text-slate-600 leading-relaxed">
            A simple tool created to turn meaningful quotes into clean, printable posters — originally built for personal use, now shared with everyone.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {features.map((feature, idx) => (
            <motion.div 
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="p-8 bg-slate-50 rounded-3xl border border-slate-100 text-center"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 bg-white rounded-xl shadow-sm mb-4">
                <SafeIcon icon={feature.icon} className="w-6 h-6 text-indigo-500" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">{feature.title}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </div>

        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="prose prose-indigo max-w-none text-slate-600"
        >
          <h2 className="text-2xl font-bold text-slate-900 mb-4">The Idea Behind QuotePoster</h2>

          <p className="mb-6">
            QuotePoster.app started with a very personal need. I wanted to display a few famous quotes in my workspace — on a digital photo frame and also as printed posters. Surprisingly, there was no simple tool that could convert quotes into clean, aesthetic posters suitable for real-world use.
          </p>

          <p className="mb-6">
            Most quote generators were made only for social media posts, had watermarks, low resolution, or required design skills. I didn’t want a complicated editor — I just wanted to type a quote and instantly get a beautiful poster I could print.
          </p>

          <p className="mb-6">
            So I built a small personal utility for myself. After using it, friends started asking for posters too. That’s when I realized many people love quotes but don’t have an easy way to turn them into something they can actually hang on a wall or display daily.
          </p>

          <p className="mb-6">
            That personal tool eventually became QuotePoster.app — a focused quote poster generator designed for speed, clarity, and real-world usage, not a complicated design platform.
          </p>

          <p className="mb-6">
            You can now use it to create printable quote posters, digital frame artwork, desk inspiration cards, affirmation prints, or social media visuals in seconds.
          </p>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">Part of LazyPanda Apps</h2>

          <p className="mb-6">
            QuotePoster.app is one of the projects under <b>LazyPanda (lazypanda.app)</b> — a collection of essential utility apps built with a simple philosophy: software should remove effort, not add complexity.
          </p>

          <p>
            Every LazyPanda app focuses on doing one job extremely well — fast, lightweight, and distraction-free. QuotePoster follows the same idea: turn meaningful words into visuals you can live with every day.
          </p>
        </motion.div>
      </div>
    </div>
  );
}

export default AboutUs;